import {forwardRef} from 'react';
import SvgIcon, {SvgIconProps} from '@material-ui/core/SvgIcon';
import {makeStyles} from '@material-ui/core/styles';
import {ReactComponent as addCircleIcon} from './add-circle.svg';
import {ReactComponent as addFolderIcon} from './add-folder.svg';
import {ReactComponent as addWaypointIcon} from './controls/add-waypoint.svg';
import {ReactComponent as copyIcon} from './copy.svg';
import {ReactComponent as allItemsIcon} from './data/all-items.svg';
import {ReactComponent as areaOutlinedIcon} from './data/areas.svg';
import {ReactComponent as arrowDropDownIcon} from './controls/arrow-drop-down.svg';
import {ReactComponent as cancelIcon} from './cancel.svg';
import {ReactComponent as centerIcon} from './controls/center.svg';
import {ReactComponent as deleteIcon} from './controls/delete.svg';
import {ReactComponent as directionsIcon} from './controls/directions.svg';
import {ReactComponent as editIcon} from './edit.svg';
import {ReactComponent as exportIcon} from './controls/export.svg';
import {ReactComponent as filterIcon} from './filter.svg';
import {ReactComponent as folderOutlineIcon} from './data/folder.svg';
import {ReactComponent as fullscreenIcon} from './controls/fullscreen.svg';
import {ReactComponent as mapLayersIcon} from './controls/map-layers.svg';
import {ReactComponent as moreIcon} from './more.svg';
import {ReactComponent as myLocationIcon} from './controls/my-location.svg';
import {ReactComponent as openDetailsIcon} from './controls/open-details.svg';
import {ReactComponent as routeOutlinedIcon} from './data/routes.svg';
import {ReactComponent as searchIcon} from './search.svg';
import {ReactComponent as shareIcon} from './controls/share.svg';
import {ReactComponent as sortIcon} from './sort.svg';
import {ReactComponent as trackOutlinedIcon} from './data/tracks.svg';
import {ReactComponent as visibilityIcon} from './controls/visibility.svg';
import {ReactComponent as visibilityOffIcon} from './controls/visibility-off.svg';
import {ReactComponent as waypointOutlinedIcon} from './data/waypoints.svg';
import {ReactComponent as zoomInIcon} from './controls/zoom-in.svg';
import {ReactComponent as zoomOutIcon} from './controls/zoom-out.svg';
import {ReactComponent as areaIcon} from './controls/area.svg';
import {ReactComponent as exploreIcon} from './controls/explore.svg';
import {ReactComponent as folderIcon} from './controls/folder.svg';
import {ReactComponent as importIcon} from './controls/import.svg';
import {ReactComponent as layersIcon} from './controls/layers.svg';
import {ReactComponent as overlaysIcon} from './controls/overlays.svg';
import {ReactComponent as printIcon} from './controls/print.svg';
import {ReactComponent as routeIcon} from './controls/route.svg';
import {ReactComponent as settingsIcon} from './controls/settings.svg';
import {ReactComponent as waypointIcon} from './controls/waypoint.svg';
import {ReactComponent as collapseIcon} from './controls/collapse.svg';
import {ReactComponent as helpIcon} from './controls/help.svg';
import {ReactComponent as alertIcon} from './alert.svg';
import {ReactComponent as facebookIcon} from './controls/facebook.svg';
import {ReactComponent as twitterIcon} from './controls/twitter.svg';
import {ReactComponent as embedIcon} from './embed.svg';
import {ReactComponent as folderSharedIcon} from './folder-shared.svg';

import {SPRING_5, GRANITE_1, SNOW_BLIND} from '@gaia/colors';

const activeColor = SPRING_5;
const defaultColor = GRANITE_1;

// Active and default styles for map control buttons. Using these on parent
// controls will allow `disabled` state styles to be inherited correctly.
export const useMapControlStyles = makeStyles({
    root: {
        color: defaultColor,
    },
    active: {
        color: activeColor,
    },
    invertedActive: {
        color: SNOW_BLIND,
    },
});

const useSvgIconStyles = makeStyles({
    // SvgIcon sets dimensions relative to font-size. We want our default
    // display size to correspond to the icon's viewbox of 24x24.
    root: {
        fontSize: 24,
    },
});

const iconNameMap = {
    'add-circle': addCircleIcon,
    'add-folder': addFolderIcon,
    'add-waypoint': addWaypointIcon,
    'all-items': allItemsIcon,
    alert: alertIcon,
    area: areaIcon,
    'area-outline': areaOutlinedIcon,
    'arrow-drop-down': arrowDropDownIcon,
    cancel: cancelIcon,
    center: centerIcon,
    collapse: collapseIcon,
    copy: copyIcon,
    delete: deleteIcon,
    directions: directionsIcon,
    edit: editIcon,
    embed: embedIcon,
    explore: exploreIcon,
    export: exportIcon,
    facebook: facebookIcon,
    filter: filterIcon,
    folder: folderIcon,
    'folder-outline': folderOutlineIcon,
    'folder-shared': folderSharedIcon,
    fullscreen: fullscreenIcon,
    help: helpIcon,
    import: importIcon,
    layers: layersIcon,
    overlays: overlaysIcon,
    print: printIcon,
    route: routeIcon,
    settings: settingsIcon,
    'map-layers': mapLayersIcon,
    more: moreIcon,
    'my-location': myLocationIcon,
    'open-details': openDetailsIcon,
    'route-outline': routeOutlinedIcon,
    search: searchIcon,
    share: shareIcon,
    sort: sortIcon,
    'track-outline': trackOutlinedIcon,
    twitter: twitterIcon,
    visibility: visibilityIcon,
    'visibility-off': visibilityOffIcon,
    waypoint: waypointIcon,
    'waypoint-outline': waypointOutlinedIcon,
    'zoom-in': zoomInIcon,
    'zoom-out': zoomOutIcon,
};

export interface GaiaIconProps extends SvgIconProps {
    name: keyof typeof iconNameMap;
}

const GaiaIcon = (props: GaiaIconProps, ref: React.Ref<SVGSVGElement>) => {
    const {root} = useSvgIconStyles();
    const {name, ...iconProps} = props;
    const iconComponent = iconNameMap[name];
    return (
        <SvgIcon
            classes={{root}}
            component={iconComponent}
            ref={ref}
            {...iconProps}
        />
    );
};

// Ref must be forwarded in order to support SubscriptionTooltip
export default forwardRef(GaiaIcon);
